import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Collapse,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import { Wrapper } from "components/wrapper";
import CustomBackdrop from "components/backdrop";

export interface CamaraColumn<T> {
  header: string;
  render: (row: T) => React.ReactNode;
  align?: "left" | "center" | "right";
}

export interface CamaraOption {
  value: string;
  label: string;
}

export interface CamaraFilter<T> {
  key: string;
  label: string;
  type: "text" | "select";
  // Para "select": opções fixas ou calculadas a partir dos dados carregados (ex.: anos disponíveis).
  options?: CamaraOption[] | ((rows: T[]) => CamaraOption[]);
  // Retorna true quando a linha atende ao valor informado (nunca chamado com valor vazio).
  match: (row: T, value: string) => boolean;
}

interface CamaraListPageProps<T> {
  title: string;
  load: () => Promise<T[]>;
  columns: CamaraColumn<T>[];
  filters?: CamaraFilter<T>[];
  rowKey: (row: T) => string | number;
  emptyMessage: string;
  // Quando informado, cada linha ganha o botão "Detalhes" que expande o conteúdo logo abaixo.
  renderDetail?: (row: T) => React.ReactNode;
  pageSize?: number;
}

export function CamaraListPage<T>({
  title,
  load,
  columns,
  filters = [],
  rowKey,
  emptyMessage,
  renderDetail,
  pageSize = 10,
}: CamaraListPageProps<T>) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [expandedKey, setExpandedKey] = useState<string | number | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    (async () => {
      try {
        setRows(await load());
      } catch (error) {
        console.log(error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () => rows.filter((row) => filters.every((f) => !values[f.key] || f.match(row, values[f.key]))),
    [rows, filters, values]
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const visible = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const columnCount = columns.length + (renderDetail ? 1 : 0);

  const setFilter = (key: string, value: string) => {
    setValues((previous) => ({ ...previous, [key]: value }));
    setPage(1);
    setExpandedKey(null);
  };

  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: title }]} />
      <PageTitle>{title}</PageTitle>
      <Wrapper>
        <Grid container spacing={4}>
          {filters.length > 0 && (
            <Grid item xs={12}>
              <Grid container spacing={3} alignItems="center">
                {filters.map((f) => {
                  const options = typeof f.options === "function" ? f.options(rows) : f.options || [];
                  return (
                    <Grid item xs={12} sm={6} md={3} key={f.key}>
                      {f.type === "text" ? (
                        <TextField
                          variant="outlined"
                          fullWidth
                          label={f.label}
                          value={values[f.key] || ""}
                          onChange={(e) => setFilter(f.key, e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      ) : (
                        <TextField
                          select
                          variant="outlined"
                          fullWidth
                          label={f.label}
                          value={values[f.key] || ""}
                          onChange={(e) => setFilter(f.key, e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          SelectProps={{ displayEmpty: true }}
                        >
                          <MenuItem value="">Todos</MenuItem>
                          {options.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Grid>
                  );
                })}
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setValues({});
                      setPage(1);
                      setExpandedKey(null);
                    }}
                  >
                    Limpar filtros
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            <CustomBackdrop open={loading} />
            {!loading && filtered.length === 0 ? (
              <Typography variant="body1">{emptyMessage}</Typography>
            ) : (
              filtered.length > 0 && (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {columns.map((c) => (
                          <TableCell key={c.header} align={c.align}>
                            {c.header}
                          </TableCell>
                        ))}
                        {renderDetail && <TableCell align="center">Detalhes</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visible.map((row) => {
                        const key = rowKey(row);
                        const expanded = expandedKey === key;
                        return (
                          <React.Fragment key={key}>
                            <TableRow>
                              {columns.map((c) => (
                                <TableCell key={c.header} align={c.align}>
                                  {c.render(row)}
                                </TableCell>
                              ))}
                              {renderDetail && (
                                <TableCell align="center">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => setExpandedKey(expanded ? null : key)}
                                  >
                                    {expanded ? "Ocultar" : "Ver"}
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                            {renderDetail && (
                              <TableRow>
                                <TableCell colSpan={columnCount} style={{ paddingTop: 0, paddingBottom: 0, borderBottom: expanded ? undefined : "none" }}>
                                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                                    <div style={{ padding: "16px 0" }}>{renderDetail(row)}</div>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            )}
          </Grid>

          {filtered.length > pageSize && (
            <Grid item xs={12}>
              <Grid container justifyContent="center">
                <Pagination
                  size="large"
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  count={pageCount}
                  page={currentPage}
                  onChange={(_, value) => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    setPage(value);
                    setExpandedKey(null);
                  }}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Wrapper>
    </>
  );
}

export const DocumentButton = ({ url, label = "Abrir" }: { url?: string | null; label?: string }) =>
  url ? (
    <Button variant="contained" color="primary" size="small" href={url} target="_blank" rel="noreferrer">
      {label}
    </Button>
  ) : (
    <span>-</span>
  );
